'use client'

import {
    ClientAppointmentView,
    createPublicFlexPayment,
    getPublicOrderMeta,
    resumePublicOrder,
    verifyPublicOrder,
} from '@/lib/api/client-portal/client-portal-api'
import { useCallback, useEffect, useState } from 'react'
import { MdBadge, MdLockOutline, MdLogout } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import { FLEX_SESSION_EXPIRED_CODE } from '@mediardex/contracts'
import { toast } from 'react-toastify'
import OrderDetailView from './order-detail-view'

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

/**
 * Chave da sessão no sessionStorage, POR LINK (publicToken). Guardar por link
 * garante que abrir OUTRO link não apareça logado — além do JWT já ser bound ao
 * token no backend. sessionStorage (não localStorage): sobrevive à volta do
 * checkout (mesma aba) e morre ao fechar a aba, sem deixar sessão em dispositivo
 * compartilhado. Guardamos o JWT, NUNCA o CPF.
 */
const sessionKey = (token: string): string => `flex-session:${token}`

/** 000.000.000-00 — mesma máscara do login do cliente. */
const formatCpf = (value: string): string => {
    const n = onlyDigits(value).slice(0, 11)
    if (n.length <= 3) return n
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9, 11)}`
}

const INPUT_CLASS =
    'w-full h-[52px] pl-12 pr-4 rounded-2xl border border-base-200 bg-base-100 text-[15px] text-base-content placeholder:text-base-content/40 outline-none transition-colors duration-200 focus:border-secondary focus:ring-4 focus:ring-secondary/12'

const PRIMARY_BTN =
    'w-full h-[52px] rounded-2xl bg-secondary text-primary-content font-semibold text-[15px] transition-all duration-300 hover:brightness-[1.05] hover:shadow-lg hover:shadow-secondary/25 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none'

/**
 * Link público exclusivo de um procedimento flex (`/p/:token`), SEM login.
 *
 * O paciente (ou quem ele autorizou a ajudar no pagamento) abre o link, confirma
 * o CPF do paciente e acompanha/paga como no portal logado. O CPF é reenviado a
 * cada ação (não há sessão); o backend revalida token+CPF sempre e tem bloqueio
 * leve contra tentativas repetidas. A tela em si é a mesma do portal — só muda a
 * origem dos dados e a autorização.
 */
export default function ClientPublicOrderPage() {
    const { token } = useParams<{ token: string }>()

    const [companyName, setCompanyName] = useState<string | null>(null)
    const [metaLoading, setMetaLoading] = useState(true)
    const [invalidLink, setInvalidLink] = useState(false)

    const [cpf, setCpf] = useState('')
    const [verifying, setVerifying] = useState(false)
    const [view, setView] = useState<ClientAppointmentView | null>(null)
    // JWT curto da sessão (em memória; a cópia persistida vive no sessionStorage).
    const [accessToken, setAccessToken] = useState<string | null>(null)

    const loadMeta = useCallback(async () => {
        if (!token) return
        try {
            const res = await getPublicOrderMeta(token)
            if (!res.success || !res.result) {
                setInvalidLink(true)
                return
            }
            setCompanyName(res.result.companyName)
        } catch {
            setInvalidLink(true)
        }
    }, [token])

    // No mount: carrega a marca e tenta RETOMAR a sessão pelo token salvo (ex.:
    // volta do checkout), pulando o portão de CPF. Token inválido/expirado/de
    // outro link cai no gate normalmente.
    const bootstrap = useCallback(async () => {
        if (!token) return
        setMetaLoading(true)
        await loadMeta()
        const saved = sessionStorage.getItem(sessionKey(token))
        if (saved) {
            try {
                const res = await resumePublicOrder(token, saved)
                if (res.success && res.result) {
                    setAccessToken(saved)
                    setView(res.result.view)
                } else {
                    sessionStorage.removeItem(sessionKey(token))
                }
            } catch {
                sessionStorage.removeItem(sessionKey(token))
            }
        }
        setMetaLoading(false)
    }, [token, loadMeta])

    useEffect(() => {
        bootstrap()
    }, [bootstrap])

    const handleVerify = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        if (onlyDigits(cpf).length !== 11) {
            toast.error('Informe um CPF válido.')
            return
        }
        setVerifying(true)
        try {
            const res = await verifyPublicOrder(token, onlyDigits(cpf))
            if (res.success && res.result) {
                sessionStorage.setItem(
                    sessionKey(token),
                    res.result.accessToken
                )
                setAccessToken(res.result.accessToken)
                setView(res.result.view)
                setCpf('')
            } else {
                toast.error(res.error || 'CPF não confere com o paciente.')
            }
        } catch {
            toast.error('Não foi possível validar. Tente novamente.')
        } finally {
            setVerifying(false)
        }
    }

    // Sair: limpa a sessão desta aba e volta ao portão de CPF — para trocar de
    // paciente/link no mesmo dispositivo sem herdar o acesso anterior.
    const handleLogout = () => {
        if (token) sessionStorage.removeItem(sessionKey(token))
        setAccessToken(null)
        setView(null)
        setCpf('')
    }

    /**
     * Sessão expirada no meio de uma ação: derruba e volta ao portão de CPF.
     *
     * Sem isto a tela ficava num beco: a sessão vencia, o backend pedia o CPF de novo,
     * e o portal só mostrava a mensagem — continuava se achando autenticado, então não
     * oferecia onde digitar o CPF nem saía sozinho. Qualquer ação seguinte repetia o
     * mesmo aviso.
     *
     * O reconhecimento é pelo CÓDIGO (`FLEX_SESSION_EXPIRED_CODE`), nunca pelo texto.
     */
    const handleExpiredSession = (): void => {
        handleLogout()
        toast.info(
            'Sua sessão expirou. Confirme o CPF novamente para continuar.'
        )
    }

    /** `true` quando a falha foi de sessão — e já tratou a queda. */
    const caiuPorSessaoExpirada = (res: {
        errorCode?: string
    }): boolean => {
        if (res.errorCode !== FLEX_SESSION_EXPIRED_CODE) return false
        handleExpiredSession()
        return true
    }

    const handleCreatePayment = async (
        amountCents: number,
        dueDate: string
    ): Promise<string | null> => {
        if (!token) return null
        try {
            const res = await createPublicFlexPayment(token, {
                // Autoriza pela sessão; sem ela (raro), cai no CPF em memória.
                accessToken: accessToken ?? undefined,
                cpf: accessToken ? undefined : onlyDigits(cpf),
                amount: amountCents,
                dueDate,
            })
            if (res.success && res.result?.billingId) {
                if (res.result.checkoutToken) {
                    // O OrderDetailView redireciona para o checkout.
                    return res.result.checkoutToken
                }
                // Sucesso sem token (raro): não redireciona, recarrega a visão.
                toast.success('Pagamento gerado. Use o link para pagar.')
                if (accessToken) {
                    const refreshed = await resumePublicOrder(
                        token,
                        accessToken
                    )
                    if (refreshed.success && refreshed.result) {
                        setView(refreshed.result.view)
                    }
                }
                return null
            }
            if (caiuPorSessaoExpirada(res)) return null
            toast.error(res.error || 'Não foi possível gerar o pagamento.')
            return null
        } catch {
            toast.error('Não foi possível gerar o pagamento.')
            return null
        }
    }

    return (
        <div
            className="min-h-screen bg-gradient-to-b from-secondary/[0.06] to-base-200/40"
            data-theme="atendimento"
        >
            <header className="sticky top-0 z-30 backdrop-blur-md bg-base-100/80 border-b border-base-200/70">
                <div className="max-w-4xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-base-100 elevation-1 flex items-center justify-center">
                        <img
                            src="/logo512.png"
                            alt="Mediar Saúde"
                            className="w-6 h-6 object-contain"
                        />
                    </div>
                    <div className="leading-none">
                        <p className="text-[15px] font-bold text-base-content tracking-tight">
                            {companyName || 'Mediar Saúde'}
                        </p>
                        <p className="text-[10px] font-semibold text-base-content/40 uppercase tracking-wide">
                            {view?.isPackage
                                ? 'Acompanhamento do pacote'
                                : 'Acompanhamento do procedimento'}
                        </p>
                    </div>
                    {/* Sair só faz sentido com sessão ativa (para trocar de
                        paciente/link no mesmo aparelho). */}
                    {view && (
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="ml-auto flex items-center gap-1.5 text-sm text-base-content/60 hover:text-error transition-colors"
                            title="Sair"
                        >
                            <MdLogout className="text-lg" />
                            <span className="hidden sm:inline">Sair</span>
                        </button>
                    )}
                </div>
            </header>

            <main className="px-4 lg:px-6 py-6 max-w-4xl mx-auto">
                {metaLoading ? (
                    <div className="flex justify-center py-16">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : invalidLink ? (
                    <div className="max-w-md mx-auto text-center py-16">
                        <h1 className="text-xl font-bold mb-2">
                            Link inválido
                        </h1>
                        <p className="text-base-content/60">
                            Este link não é válido ou expirou. Procure a
                            recepção para obter um novo.
                        </p>
                    </div>
                ) : view ? (
                    <OrderDetailView
                        view={view}
                        onCreatePayment={handleCreatePayment}
                    />
                ) : (
                    // Portão de CPF
                    <div className="max-w-md mx-auto mt-8">
                        <div className="bg-base-100 rounded-3xl border border-base-200 elevation-1 p-7">
                            <h1 className="text-xl font-bold text-base-content mb-1">
                                Confirme seu CPF
                            </h1>
                            <p className="text-sm text-base-content/60 mb-6">
                                Para acompanhar e pagar o atendimento, informe o
                                CPF do paciente.
                            </p>
                            <form
                                onSubmit={handleVerify}
                                className="flex flex-col gap-4"
                            >
                                <div className="relative">
                                    <MdBadge className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-base-content/40" />
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className={INPUT_CLASS}
                                        value={cpf}
                                        onChange={e =>
                                            setCpf(formatCpf(e.target.value))
                                        }
                                        placeholder="CPF do paciente"
                                        autoFocus
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className={PRIMARY_BTN}
                                    disabled={verifying}
                                >
                                    {verifying ? 'Validando...' : 'Continuar'}
                                </button>
                            </form>
                            <p className="flex items-center gap-1.5 justify-center text-xs text-base-content/40 mt-5">
                                <MdLockOutline className="text-sm" />
                                Acesso protegido por link exclusivo e CPF
                            </p>
                        </div>
                    </div>
                )}
            </main>
        </div>
    )
}
